import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Section components
import Hero from '../../components/sections/Hero';
import FeaturedSection from '../../components/sections/FeaturedSection';
import ServicesGrid from '../../components/sections/ServicesGrid';
import TestimonialsSection from '../../components/sections/TestimonialsSection';
import CallToAction from '../../components/sections/CallToAction';

// UI components
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

// Mock data
import { 
  services,
  testimonials, 
  caseStudies, 
  stats, 
  blogPosts, 
  targetAudiences 
} from '../../data/mockData';

/**
 * Home page component with professional theme
 */
const HomePage: React.FC = () => {
  return (
    <Layout>
      {/* Hero section with professional gradient */}
      <div className="hero-gradient">
        <div className="relative py-20 md:py-32">
          <Container>
            <div className="max-w-4xl mx-auto text-center">
              <div className="badge-primary mb-4">
                AI Solutions That Work
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Transform Your Business with{' '}
                <span className="text-gradient">AI Excellence</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                We help businesses unlock the power of artificial intelligence through expert training, 
                seamless implementation, and continuous optimization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => window.location.href = '/contact'}
                >
                  Get Free Consultation
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="lg"
                  onClick={() => window.location.href = '/services'}
                >
                  Explore Services
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>

      {/* Services section */}
      <Section className="section-alt">
        <Container>
          <div className="text-center mb-16">
            <div className="badge-primary mb-4">Our Services</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comprehensive AI Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From training to implementation, we provide end-to-end AI solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full card-hover p-6">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link 
                    to={service.url} 
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Learn more
                    <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Stats section */}
      <Section className="bg-primary-600 text-white">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-white/80 text-sm md:text-base">
                  {stat.label}
                  {stat.unit && <div className="text-xs mt-1">{stat.unit}</div>}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Target audiences section */}
      <Section>
        <Container>
          <div className="text-center mb-16">
            <div className="badge-success mb-4">For Everyone</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              AI Solutions That Scale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From individual professionals to enterprise organizations, we adapt our solutions to your specific needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {targetAudiences.slice(0, 2).map((audience, index) => (
              <motion.div
                key={audience.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card className="h-full card-hover p-6">
                  <div className="flex items-start mb-6">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-4 0h-4m-4 0H3m15 0v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5m11-13.5V9" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 ml-4 mt-2">{audience.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{audience.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {audience.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    variant="outline-primary" 
                    onClick={() => window.location.href = audience.ctaLink} 
                    className="w-full"
                  >
                    {audience.ctaText}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Testimonials section */}
      <Section className="section-alt">
        <Container>
          <div className="text-center mb-16">
            <div className="badge-secondary mb-4">Testimonials</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Success stories from businesses that have transformed their operations with our AI solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full p-6">
                  <div className="flex items-start mb-4">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0"></div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900">{testimonial.author}</h4>
                      <p className="text-sm text-gray-600">{testimonial.title}, {testimonial.company}</p>
                    </div>
                  </div>
                  
                  <div className="flex text-yellow-400 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"{testimonial.quote}"</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Case studies preview */}
      <Section>
        <Container>
          <div className="text-center mb-16">
            <div className="badge-warning mb-4">Case Studies</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world results from our AI implementations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseStudies.slice(0, 3).map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card className="h-full card-hover">
                    <div className="aspect-video bg-gray-100 rounded-t-lg overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 opacity-80 flex items-center justify-center">
                        <span className="text-white text-lg font-semibold">{study.industry}</span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="badge-primary mb-3">
                        {study.solution}
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{study.title}</h3>
                      <p className="text-gray-600 mb-4">{study.excerpt}</p>
                      
                      <h4 className="font-semibold text-sm text-gray-900 mb-2">Key Results:</h4>
                      <ul className="space-y-1 mb-4">
                        {study.results.map((result, idx) => (
                          <li key={idx} className="flex items-start text-sm">
                            <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-auto pt-2">
                        <span className="inline-flex items-center text-primary-600 font-medium">
                          Read full case study
                          <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              variant="primary" 
              onClick={() => window.location.href = '/case-studies'}
            >
              View All Case Studies
            </Button>
          </div>
        </Container>
      </Section>

      {/* Blog preview */}
      <Section className="section-alt">
        <Container>
          <div className="text-center mb-16">
            <div className="badge-info mb-4">Latest Insights</div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Stay Ahead with AI Knowledge
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert insights, trends, and strategies to help you succeed in the AI-driven future
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.slice(0, 3).map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={post.url} className="block h-full">
                  <Card className="h-full card-hover">
                  <div className="aspect-video bg-gray-100 rounded-t-lg overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-orange-500 to-amber-600 opacity-80 flex items-center justify-center">
                      <span className="text-white font-medium">{post.category}</span>
                    </div>
                  </div>
                    
                    <div className="p-6">
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <span>{post.publishDate}</span>
                        <span className="mx-2">•</span>
                        <span>{post.readTime}</span>
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{post.title}</h3>
                      <p className="text-gray-600 mb-4">{post.excerpt}</p>
                      
                      <div className="flex items-center mt-auto pt-2">
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex-shrink-0"></div>
                        <span className="ml-2 text-sm font-medium text-gray-700">{post.author}</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              variant="primary" 
              onClick={() => window.location.href = '/blog'}
            >
              Read More Articles
            </Button>
          </div>
        </Container>
      </Section>

      {/* Call to action */}
      <Section className="bg-primary-600 text-white">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Business with AI?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Get started with a free consultation to discuss how our AI solutions can address your specific business challenges and unlock new opportunities.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                variant="secondary" 
                size="lg" 
                onClick={() => window.location.href = '/contact'}
              >
                Schedule Free Consultation
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.href = '/about'}
              >
                Learn About Our Approach
              </Button>
            </div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default HomePage;